import { Controller } from "stimulus";
import { EventGeckoChartNavigatorAdjusted, EventGeckoChartRendered } from "../../events";
import { createPopper } from "@popperjs/core";
import { useClickOutside } from "stimulus-use";
import { loadFlatpickr } from "../../util/load_package";

export default class extends Controller {
  static targets = [
    "chart", "rangeSelector", "calendarButton",
    "exportButton", "exportDropdownMenu"
  ];

  chartInstance = null;
  chartUrls = null;

  calendarInstance = null;
  selectedRange = null;

  connect() {
    if (this.hasExportButtonTarget) {
      useClickOutside(this, { element: this.exportButtonTarget });
    }
    window.addEventListener(EventGeckoChartRendered, this.onChartRerender.bind(this));

    this.chartUrls = JSON.parse(this.element.dataset.chartUrls);
    this.selectedRange = this.element.dataset.defaultRange;

    if (this.hasCalendarButtonTarget) {
      window.addEventListener(EventGeckoChartNavigatorAdjusted, this.onChartNavigatorAdjustment.bind(this));
    }
  }

  handleRangeOptionChange(e) {
    const rangeKey = e.currentTarget.dataset.key;

    if (this.selectedRange === rangeKey) {
      return;
    }

    this.selectedRange = rangeKey;
    this.refreshChartSettings();
  }

  handleCalendarChange(selectedDates) {
    // Our Flatpickr is a range selector, so we need to check whether the returned array has two dates (to, from).
    if (!selectedDates || selectedDates.length !== 2) {
      return;
    }

    this.selectedRange = "custom";

    const [fromDate, toDate] = selectedDates;

    fromDate.setHours(0, 0, 0);
    toDate.setHours(23, 59, 59);

    this.chartInstance.xAxis[0].setExtremes(fromDate.getTime(), toDate.getTime(), true, false, { trigger: "calendar" });
  }

  toggleCalendar(e) {
    if (this.calendarOpen === true) {
      this.calendarInstance.close();
      this.calendarOpen = false;
    } else {
      this.calendarButtonTarget.classList.add("selected");
      this.calendarInstance.open();
      this.calendarOpen = true;
    }
  }

  async refreshCalendarSettings() {
    if (!this.calendarInstance) {
      const Flatpickr = await loadFlatpickr();

      this.calendarInstance = Flatpickr(this.calendarButtonTarget, {
        position: "auto left",
        mode: "range",
        onChange: this.handleCalendarChange.bind(this),
        onClose: () => {
          this.calendarOpen = false;
          this.calendarButtonTarget.classList.remove("selected");
        }
      });
    }

    const mainDataAxis = this.chartInstance.xAxis[0].getExtremes();
    const maxDataAxis = this.chartInstance.navigator?.xAxis || mainDataAxis;
    const { min: selectedRangeFrom, max: selectedRangeTo } = mainDataAxis;
    const { min: allowedRangeFrom, max: allowedRangeTo } = maxDataAxis;

    this.calendarInstance.config.minDate = allowedRangeFrom;
    this.calendarInstance.config.maxDate = allowedRangeTo;
    this.calendarInstance.setDate([selectedRangeFrom, selectedRangeTo], false);
  }

  showExportDropdown(e) {
    if (!this.hasExportDropdownMenuTarget) {
      return;
    }

    e.stopPropagation();

    if (this.exportDropdownPopperInstance) {
      return this.hideExportDropdown(e);
    }

    this.exportButtonTarget.classList.add("selected");
    this.exportDropdownMenuTarget.classList.remove("tw-hidden");
    this.exportDropdownPopperInstance = createPopper(this.exportButtonTarget, this.exportDropdownMenuTarget, { placement: "bottom-start" });
  }

  hideExportDropdown(e) {
    if (!this.exportDropdownPopperInstance) {
      return;
    }

    this.exportButtonTarget.classList.remove("selected");
    this.exportDropdownMenuTarget.classList.add("tw-hidden");
    this.exportDropdownPopperInstance?.destroy();
    this.exportDropdownPopperInstance = null;
  }

  refreshChartSettings() {
    this.chartTarget.dataset.dataUrl = this.chartUrls?.[this.selectedRange];

    if (this.chartTarget.dataset.controller === "coins-chart") {
      this.chartTarget.dataset.coinsChartRerenderValue = true;
    } else {
      this.chartTarget.dataset.priceChartRerenderValue = true;
    }
  }

  onChartRerender(e) {
    // global-charts#index page has multiple charts, so we need to check if the event is for this chart.
    if (e.target.id !== this.chartTarget.id) {
      return;
    }

    this.chartInstance = e.detail.chart;

    if (this.hasCalendarButtonTarget) {
      this.refreshCalendarSettings();
    }
  }

  onChartNavigatorAdjustment(e) {
    if (e.target.id !== this.chartTarget.id || e.detail.trigger === "calendar") {
      return;
    }

    this.refreshCalendarSettings();
  }
}
